/* Messages — the hub (migration pack messages, v3.3 WF-026 · 027 · 028 · 030 · 125).
   One « Messages » in the nav, one page with the reference's tab strip adapted to what exists and is ruled:
   Retours privés · Activité · E-mails aux clients · Mes notifications. The reference's « Expéditeur » tab is a
   later phase (WF-029): it is not drawn, the e-mails tab says so in one honest line.

   « Retours privés » (WF-026): what a customer wrote to the merchant and to nobody else. A retour privé is ONE-WAY —
   never published, never exported with the reviews, no reply channel here: the seam has `markRead`, `markTreated`
   and `noteMessage`, and deliberately nothing that sends. The note belongs to the merchant and never leaves the screen.
   WF-125 / contract 11.3: a manager keeps reading the retours of her establishments (`visibleMessages`, lib-pack-17);
   the reference's refusal page is NOT adopted — a scoped role sees her establishments' inbox, rendered as such.

   « Activité » (WF-027, lib pack 3): contract 11's feed, the facts the server writes where they happen (quota, a
   gift at its cap, a retour, a campaign's end, a cancelled gift), the same feed the bell counts. Opening the tab
   reads what it shows: the unread facts are marked read once and keep « nouveau » for the visit.

   « E-mails aux clients » (WF-028, the automatic half): routes/messages/EmailsClients.tsx.
   « Mes notifications » (WF-030): routes/messages/Preferences.tsx, embedded. */
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, EmptyState, ErrorState, Field, Input, List, ListRow, Note, PageHeader, Pill, Select, Textarea, Tabs, useToast } from '@/ui'
import { apiWrites, markNotificationsRead, useCampaigns, useLocations, useNotifications, type NotificationItem } from '@/lib/api'
import { markRead, markTreated, noteMessage, visibleMessages, type PrivateMessage } from '@/lib/messages'
import { getCampaign } from '@/lib/campaign'
import { factsFor, quandParis, unreadActivite } from '@/lib/notifications'
import './messages.css'
import { currentUser, isScoped } from '@/lib/scope'
import Preferences, { NOTIFICATION_LABELS } from './Preferences'
import EmailsClients from './EmailsClients'
import { Chargement, Lu, quand } from './parts'

type Onglet = 'retours' | 'activite' | 'emails' | 'notifications'
type Traitement = 'tous' | 'a-traiter' | 'traites'
type Lecture = 'tous' | 'lus' | 'non-lus'

/** « 7 octobre » — the day a fact happened, on the merchant's clock. */
const jour = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' })
/** « 2 résultats » / « 1 résultat » / « 0 résultat ». */
const resultats = (n: number) => `${n} résultat${n > 1 ? 's' : ''}`
/** A plain, accent-free lower-case key for the search boxes. */
const plat = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

/** What one fact says, and the one door that resolves it. The five mail kinds take their name and sentence from
    Préférences (one copy of the words); `quota_warning` is never mailed, so its words are here. Details come from
    the fact's `meta` (11.1) when the server sent them, never invented. */
function fact(i: NotificationItem, names: { campaign: (id?: string | null) => string | null; location: (id?: string | null) => string | null }, owner: boolean) {
  const m = (i.meta ?? {}) as Record<string, unknown>
  const str = (k: string) => (typeof m[k] === 'string' && m[k] !== '' ? String(m[k]) : null)
  const num = (k: string) => (typeof m[k] === 'number' ? Number(m[k]) : null)
  const lieu = str('locationName') ?? names.location(i.locationId ?? str('locationId'))
  const tout = (...xs: Array<string | null | false>) => xs.filter(Boolean) as string[]
  switch (i.kind) {
    case 'quota_warning': {
      const pct = num('percent') ?? 80, used = num('used'), quota = num('quota')
      return {
        title: pct >= 100 ? 'Quota de participations atteint' : `Quota de participations à ${pct} %`,
        details: tout(used != null && quota != null && `${used} participations sur ${quota}`, lieu),
        what: 'Vos campagnes continuent. Au plafond, le quota plus 20 %, elles se mettent en pause jusqu’au 1ᵉʳ.',
        /* Version 17: Offre and its sections are the owner's; an admin reads the fact with no door (lib-pack-15) */
        door: owner ? { label: 'Voir l’utilisation', to: '/parametres/utilisation' } : undefined,
      }
    }
    case 'quota_paused':
      return { title: NOTIFICATION_LABELS.quota_paused.label, details: tout(`depuis le ${jour(i.at)}`, lieu),
        what: NOTIFICATION_LABELS.quota_paused.what, door: owner ? { label: 'Voir mon offre', to: '/parametres/offre' } : undefined }
    case 'slice_capped': {
      const cap = num('cap')
      return { title: NOTIFICATION_LABELS.slice_capped.label, details: tout(str('label'), cap != null && `plafond de ${cap} par mois`),
        what: NOTIFICATION_LABELS.slice_capped.what, door: { label: 'Voir les cadeaux', to: '/cadeaux' } }
    }
    case 'feedback_received':
      return { title: NOTIFICATION_LABELS.feedback_received.label, details: tout(lieu),
        what: NOTIFICATION_LABELS.feedback_received.what, door: { label: 'Lire', to: '?onglet=retours' } }
    case 'campaign_ended': {
      const id = i.campaignId ?? str('campaignId'), p = num('participations'), g = num('gifts')
      return { title: NOTIFICATION_LABELS.campaign_ended.label,
        details: tout(str('name') ?? names.campaign(id), p != null && `${p} participation${p > 1 ? 's' : ''}`, g != null && `${g} cadeau${g > 1 ? 'x' : ''}`),
        what: NOTIFICATION_LABELS.campaign_ended.what, door: { label: 'Voir la campagne', to: id ? `/campagnes/${id}` : '/campagnes' } }
    }
    case 'prize_cancelled': {
      const code = i.prizeCode ?? str('prizeCode'), why = str('reason')
      return { title: NOTIFICATION_LABELS.prize_cancelled.label, details: tout(str('label'), code && `code ${code}`, why && `« ${why} »`),
        what: NOTIFICATION_LABELS.prize_cancelled.what, door: { label: 'Voir les cadeaux gagnés', to: '/resultats?vue=cadeaux' } }
    }
  }
}

/** The title and the one-line description of each tab — the reference's words where they fit what the app does. */
const TITRES: Record<Onglet, { titre: string; sous: (prenom: string) => string }> = {
  retours: { titre: 'Retours privés', sous: () => 'Lire, prendre une note, puis marquer comme traité quand vous avez terminé.' },
  activite: { titre: 'Activité de l’établissement', sous: () => 'Retrouvez les événements enregistrés sur votre compte ces 7 derniers jours.' },
  emails: { titre: 'E-mails aux clients', sous: () => 'Vérifiez et retrouvez chaque message automatique. Aucun envoi réel depuis cette page.' },
  notifications: { titre: 'Mes notifications', sous: prenom => `Préférences personnelles de ${prenom}.` },
}

export default function Messages() {
  const { data, isPending, isError, refetch } = useNotifications()
  const { data: campaigns } = useCampaigns()
  const { data: locations } = useLocations()
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const [nouveaux, setNouveaux] = useState<Set<string> | null>(null)   // the facts unread when the tab opened
  const marked = useRef(false)                                           // one POST per visit, even when an effect runs twice
  /* WF-026 · the filters of the inbox */
  const [recherche, setRecherche] = useState('')
  const [traitement, setTraitement] = useState<Traitement>('tous')
  const [lecture, setLecture] = useState<Lecture>('tous')
  const [plusFiltres, setPlusFiltres] = useState(false)
  const [campagne, setCampagne] = useState('toutes')
  const [brouillons, setBrouillons] = useState<Record<string, string>>({})   // the notes being written, per message
  /* WF-027 · the filters of the feed */
  const [rechercheActivite, setRechercheActivite] = useState('')
  const [plusFiltresActivite, setPlusFiltresActivite] = useState(false)
  const [genre, setGenre] = useState('tous')
  const [tick, setTick] = useState(0); void tick               // the seam is localStorage
  const nav = useNavigate()
  const toast = useToast()

  const user = currentUser()
  const scoped = isScoped(user)
  const owner = user.role === 'owner'
  const unscoped = user.role === 'owner' || user.role === 'admin'

  /* `?vue=` is accepted beside `?onglet=` so a deep link from another screen lands on the inbox whichever spelling
     it used. The e-mails and the notifications tabs are the owner's and an admin's (the mail leaves in their name, the
     preferences are theirs — hide-not-refuse, roles.ts); a scoped role asking for them lands on her activity. */
  const demande = params.get('onglet') ?? params.get('vue')
  const onglet: Onglet = demande === 'retours' ? 'retours'
    : demande === 'emails' && unscoped ? 'emails'
    : demande === 'notifications' && unscoped ? 'notifications'
    : 'activite'
  const setOnglet = (o: string) => { const p = new URLSearchParams(params); p.set('onglet', o); setParams(p) }

  const retours = visibleMessages() /* WF-125 · a scoped role reads her establishments' retours */
  const nonLus = retours.filter(m => !m.read).length
  const facts = factsFor(data)
  const activite = unreadActivite(facts)

  /* F-8 · the tab reads what it shows, once per visit: the bell drops by what was just read, the rows keep
     « nouveau » until the merchant leaves. A failed mark changes nothing on screen; the next visit tries again. */
  useEffect(() => {
    if (onglet !== 'activite' || !data || nouveaux || marked.current) return
    marked.current = true
    const ids = facts.filter(i => !i.readAt).map(i => i.id)
    setNouveaux(new Set(ids))
    if (ids.length) markNotificationsRead(ids).then(() => qc.invalidateQueries({ queryKey: ['notifications'] }), () => {})
  }, [onglet, data, nouveaux, facts, qc])

  const onglets: [string, string][] = [
    ['retours', `Retours privés${nonLus ? ` · ${nonLus} non lus` : ''}`],
    ['activite', `Activité${activite ? ` · ${activite}` : ''}`],
    ...(unscoped ? [['emails', 'E-mails aux clients'], ['notifications', 'Mes notifications']] as [string, string][] : []),
  ]
  const entete = (
    <PageHeader crumb={<span>Messages</span>} title={TITRES[onglet].titre}
      description={TITRES[onglet].sous(user.name)}
      tabs={<Tabs value={onglet} onChange={setOnglet} options={onglets} />} />
  )

  /* ================= E-mails aux clients (WF-028) · Mes notifications (WF-030) ================= */
  if (onglet === 'emails') return <div className="page">{entete}<EmailsClients /></div>
  if (onglet === 'notifications') return <div className="page">{entete}<Preferences embarque /></div>

  /* ================= Retours privés (WF-026) ================= */
  if (onglet === 'retours') {
    const campagnes = Array.from(new Set(retours.map(m => m.campaign)))
    const q = plat(recherche.trim())
    const liste = retours.filter(m =>
      (traitement === 'tous' || (traitement === 'traites') === !!m.treated)
      && (lecture === 'tous' || (lecture === 'lus') === m.read)
      && (campagne === 'toutes' || m.campaign === campagne)
      && (!q || plat(`${m.from ?? 'Anonyme'} ${m.text} ${m.campaign} ${m.note ?? ''}`).includes(q)))
    const aTraiter = retours.filter(m => !m.treated).length

    const lire = (m: PrivateMessage, read: boolean) => {
      markRead(m.id, read); setTick(n => n + 1)
      toast({ message: read ? 'Marqué comme lu.' : 'Remis en non lu.' })
    }
    const traiter = (m: PrivateMessage, treated: boolean) => {
      markTreated(m.id, treated); setTick(n => n + 1)
      toast({ message: treated ? 'Marqué comme traité.' : 'Remis à traiter.' })
    }
    const brouillon = (m: PrivateMessage) => brouillons[m.id] ?? m.note ?? ''
    const enregistrerNote = (m: PrivateMessage) => {
      const note = brouillon(m).trim()
      noteMessage(m.id, note); setBrouillons(b => { const { [m.id]: _, ...rest } = b; void _; return rest }); setTick(n => n + 1)
      toast({ message: note ? 'Note enregistrée — rien que pour vous.' : 'Note effacée.' })
    }

    return (
      <div className="page">
        {entete}

        {retours.length === 0 ? (
          <EmptyState
            title="Aucun retour privé pour l’instant."
            why={scoped
              ? 'Vous lisez ici les retours privés de vos établissements : une cliente ne peut écrire que si une campagne propose un retour privé.'
              : 'Une cliente ne peut vous écrire ici que si une de vos campagnes propose un retour privé : choisissez « Un retour privé » sur une visite.'}
            action={scoped ? undefined : <Button variant="primary" size="sm" onClick={() => nav('/campagnes')}>Vos campagnes</Button>}>
            <p>
              Un retour privé, c’est ce qu’une cliente vous dirait si elle osait&nbsp;: l’attente
              d’un samedi, la musique trop forte, le soin qu’elle cherchait sans le trouver.
              Ce message ne part nulle part ailleurs — il n’est <b>jamais publié</b>, jamais
              exporté avec vos avis, et personne d’autre que vous ne le lit.
            </p>
          </EmptyState>
        ) : (
          <>
            <Card data-testid="retours-filtres">
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <Field label="Rechercher un retour">{id => <Input id={id} value={recherche} onChange={e => setRecherche(e.target.value)} placeholder="Nom, mot-clé…" />}</Field>
                <Field label="Traitement">{id => <Select id={id} value={traitement} onChange={e => setTraitement(e.target.value as Traitement)}>
                  <option value="tous">Tous les retours</option><option value="a-traiter">À traiter</option><option value="traites">Traités</option></Select>}</Field>
                <Field label="Lecture">{id => <Select id={id} value={lecture} onChange={e => setLecture(e.target.value as Lecture)}>
                  <option value="tous">Lus et non lus</option><option value="lus">Lus</option><option value="non-lus">Non lus</option></Select>}</Field>
                <Button size="sm" variant="quiet" aria-expanded={plusFiltres} onClick={() => setPlusFiltres(v => !v)}>{plusFiltres ? 'Moins de filtres' : 'Plus de filtres'}</Button>
                {plusFiltres && (
                  <Field label="Campagne">{id => <Select id={id} value={campagne} onChange={e => setCampagne(e.target.value)}>
                    <option value="toutes">Toutes les campagnes</option>{campagnes.map(c => <option key={c} value={c}>{c}</option>)}</Select>}</Field>
                )}
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
                <span className="f-help" data-testid="retours-compte">{resultats(liste.length)} · du plus récent au plus ancien</span>
                <span className="f-help" style={{ marginLeft: 'auto' }}><b>{aTraiter ? `${aTraiter} à traiter` : 'Tout est traité'}</b></span>
              </div>
              {scoped && <p className="f-help" style={{ margin: '8px 0 0' }} data-testid="retours-perimetre">Vous lisez les retours privés de vos établissements.</p>}
            </Card>

            {liste.length === 0 ? (
              <EmptyState title="Aucun retour ne correspond à ces filtres."
                why={traitement === 'a-traiter' && aTraiter === 0 ? 'Rien n’attend votre suite — les retours restent consultables sous « Tous les retours ».' : 'Élargissez la recherche ou remettez les filtres à « Tous ».'} />
            ) : liste.map(m => {
              const texte = brouillon(m)
              const dirty = texte !== (m.note ?? '')
              return (
                <Card key={m.id} data-testid="retour-ligne">
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {m.treated ? <Pill tone="done">traité</Pill> : <Pill tone="warn" mark="●">à traiter</Pill>}
                    <Lu read={m.read} />
                    {/* the glyph says what this IS — a message for the merchant only */}
                    <Pill tone="ink" mark="🔒">privé</Pill>
                  </div>
                  <p className="t-body private-note" style={{ margin: '10px 0 4px' }}>« {m.text} »</p>
                  <p className="f-help" style={{ margin: '0 0 12px' }}>{m.from ?? 'Anonyme'} · {m.campaign} · {quand(m.at)}</p>
                  <Field label="Note interne — jamais envoyée" help="Pour vous et votre équipe. Elle n’est jamais envoyée à la cliente.">
                    {id => <Textarea id={id} rows={2} value={texte} onChange={e => { const v = e.currentTarget.value; setBrouillons(b => ({ ...b, [m.id]: v })) }}
                      placeholder="Vu avec Nadia — on décale les rendez-vous du samedi de 10 min." />}
                  </Field>
                  <div className="actions" style={{ marginTop: 10 }}>
                    <Button variant="primary" size="sm" disabled={!dirty} onClick={() => enregistrerNote(m)}>Enregistrer la note</Button>
                    <Button size="sm" variant="quiet" onClick={() => traiter(m, !m.treated)}>{m.treated ? 'Remettre à traiter' : 'Marquer comme traité'}</Button>
                    <Button size="sm" variant="quiet" onClick={() => lire(m, !m.read)}>{m.read ? 'Marquer non lu' : 'Marquer comme lu'}</Button>
                  </div>
                </Card>
              )
            })}

            {liste.length > 0 && (
              <Card data-testid="retours-contexte">
                <h2 className="t-page" style={{ margin: '0 0 10px', fontSize: 17 }}>Contexte des retours</h2>
                <div style={{ display: 'grid', gap: 6 }}>
                  {liste.map(m => (
                    <p key={m.id} className="t-body" style={{ margin: 0 }}>
                      {m.from && m.from !== 'Anonyme' ? m.from : 'Contribution anonyme'} · <Pill tone="ink">{m.campaign}</Pill> · {quand(m.at)}
                    </p>
                  ))}
                </div>
                <p className="f-help" style={{ margin: '10px 0 0' }}>Aucune identité n’est reconstituée pour un retour anonyme. Seuls le prénom et l’initiale laissés par la cliente apparaissent ici.</p>
              </Card>
            )}

            <Note tone="candour">
              Un retour privé est à sens unique&nbsp;: il n’est jamais publié, jamais exporté avec
              vos avis, et il n’y a pas de bouton pour répondre ici. Si vous voulez recontacter
              une cliente, faites-le comme vous le feriez d’habitude.
            </Note>
          </>
        )}
      </div>
    )
  }

  /* ================= Activité (WF-027) ================= */
  if (isPending) return <div className="page">{entete}<Chargement /></div>
  if (isError) return (
    <div className="page">
      {entete}
      <ErrorState what="Impossible de charger votre activité."
        lost="Votre connexion a peut-être sauté — rien n’a été perdu."
        action={<Button variant="primary" size="sm" onClick={() => refetch()}>Réessayer</Button>} />
    </div>
  )

  /* ---------- EM-10 · nothing happened: say what would show here ---------- */
  if (params.get('etat') === 'vide' || facts.length === 0) {
    return (
      <div className="page">
        {entete}
        <EmptyState
          title="Aucune activité pour le moment."
          why={unscoped
            ? 'Les éléments enregistrés apparaîtront ici : un quota qui approche, une campagne en pause ou terminée, un cadeau au plafond ou annulé, un nouveau retour privé.'
            : 'Les éléments enregistrés apparaîtront ici : les nouveaux retours privés de vos établissements.'}
          action={unscoped
            ? <Button variant="quiet" size="sm" onClick={() => setOnglet('notifications')}>Régler mes notifications</Button> : undefined} />
      </div>
    )
  }

  const names = {
    /* the ids row: with an API every campaign is the server's (getCampaign), the fixture list is the demo's */
    campaign: (id?: string | null) => (id ? (apiWrites ? getCampaign(id)?.name : campaigns?.find(c => c.id === id)?.name) ?? null : null),
    location: (id?: string | null) => (id ? locations?.find(l => l.id === id)?.name ?? null : null),
  }
  const lignes = facts.map(i => ({ i, f: fact(i, names, owner) }))
  const genres = Array.from(new Set(lignes.map(({ f }) => f.title)))
  const qa = plat(rechercheActivite.trim())
  const visibles = lignes.filter(({ f }) =>
    (genre === 'tous' || f.title === genre)
    && (!qa || plat(`${f.title} ${f.details.join(' ')} ${f.what}`).includes(qa)))

  return (
    <div className="page">
      {entete}
      <Card data-testid="activite-filtres">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="Rechercher une activité">{id => <Input id={id} value={rechercheActivite} onChange={e => setRechercheActivite(e.target.value)} placeholder="Nom, mot-clé…" />}</Field>
          <Button size="sm" variant="quiet" aria-expanded={plusFiltresActivite} onClick={() => setPlusFiltresActivite(v => !v)}>{plusFiltresActivite ? 'Moins de filtres' : 'Plus de filtres'}</Button>
          {plusFiltresActivite && (
            <Field label="Type d’événement">{id => <Select id={id} value={genre} onChange={e => setGenre(e.target.value)}>
              <option value="tous">Tous les événements</option>{genres.map(g => <option key={g} value={g}>{g}</option>)}</Select>}</Field>
          )}
        </div>
        <p className="f-help" style={{ margin: '10px 0 0' }} data-testid="activite-compte">{resultats(visibles.length)} · les 7 derniers jours, du plus récent au plus ancien</p>
      </Card>
      {visibles.length === 0 ? (
        <EmptyState title="Aucune activité ne correspond à cette recherche." why="Élargissez la recherche ou remettez le type à « Tous les événements »." />
      ) : (
        <List>
          {visibles.map(({ i, f }) => {
            const nouveau = nouveaux ? nouveaux.has(i.id) : !i.readAt
            return (
              <ListRow key={i.id} title={f.title}
                meta={<>{[...f.details, quandParis(i.at)].join(' · ')}{nouveau && <> · <Pill tone="warn" mark="●">nouveau</Pill></>}<br />{f.what}</>}
                end={f.door && <Button size="sm" variant="quiet"
                  onClick={() => (f.door!.to.startsWith('?') ? setOnglet('retours') : nav(f.door!.to))}>{f.door.label}</Button>} />
            )
          })}
        </List>
      )}
    </div>
  )
}
